import LoadingSpinner from "@components/loadingSpinner";
import { postDiscount } from "@features/cartDetails/utils/discountUtil";
import { Button, Heading, Text } from "@components/theming";
import { useNotificationStore } from "@store/notificationStore";
import { useState } from "react";
import { Discounts } from "../types";
import DiscountDropdown from "./discountDropdown";
import DiscountMembershipDropdown from "./discountMembershipDropdown";
import { useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";
import { TextInput } from "@components/inputs";
import { joinClassNames } from "@utils/helpers";

interface Props {
  discounts: Discounts[];
}

const DiscountList = ({ discounts }: Props) => {
  const t = useTranslations("Checkout.Cart.tickets.discount");
  const [loading, setLoading] = useState(false);
  const [membershipDiscount, setMembershipDiscount] = useState(
    Object.fromEntries(
      discounts.map((discount) => [
        discount.Id,
        {
          UsernameLabel: "",
          PasswordLabel: "",
        },
      ])
    )
  );
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const isShopsite = useIsShopsite();

  const handleDiscount = async (
    discountId: number,
    isRetro: boolean,
    paQty?: string
  ) => {
    await postDiscount(
      membershipDiscount,
      setLoading,
      setSuccess,
      setFailure,
      discountId,
      isRetro,
      paQty,
      t
    );
  };

  return (
    <div>
      <Heading
        as="h3"
        size="4"
        sharedClasses="mb-1"
        shopsiteClasses="font-medium"
      >
        {t("heading")}
      </Heading>
      <Text size="2" shopsiteClasses="text-sm">
        {t.rich("subheading", {
          strong: (chunks) => (
            <Text sharedClasses="font-semibold">{chunks}</Text>
          ),
        })}
      </Text>
      <ul
        role="list"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-4 sm:mt-6 w-full"
      >
        {discounts.map((discount) => (
          <li
            key={discount.Id}
            className={joinClassNames(
              "col-span-1",
              isShopsite ? "rounded-lg" : "rounded-[var(--radius-4)]"
            )}
          >
            <div
              className={joinClassNames(
                "shadow",
                isShopsite
                  ? "rounded-lg bg-indigo-50 ring-2 ring-indigo-500 px-4 py-2 sm:px-6 sm:py-3"
                  : "bg-[color:var(--accent-a3)] border border-[color:var(--accent-a9)] rounded-[var(--radius-4)] p-[var(--space-4)]"
              )}
            >
              <Heading
                as="h3"
                size="3"
                weight="bold"
                shopsiteClasses="text-base font-semibold leading-6"
              >
                {discount.Name}
              </Heading>
              <div className="mt-2 sm:flex sm:items-start flex-col sm:justify-between">
                {discount.Description && (
                  <Text
                    as="p"
                    size="2"
                    sharedClasses="block max-w-xl"
                    shopsiteClasses="text-gray-500 text-sm"
                  >
                    {discount.Description}
                  </Text>
                )}
                <div className="mt-2 sm:mt-2 flex flex-shrink-0 items-center w-full">
                  {discount.Membership &&
                  discount.QtyRules.length > 0 &&
                  discount.QtyRules[0]?.Max !== discount.QtyRules[0]?.Min ? (
                    <DiscountMembershipDropdown
                      key={`${discount.Id}-${discount.QtyRules[0].Max}-${discount.QtyRules[0].Title}`}
                      qtyRules={discount.QtyRules[0]}
                      discountId={discount.Id}
                      isRetro={discount.IsRetrospectiveCombo}
                      loading={loading}
                      handleDiscount={handleDiscount}
                      memberShips={discount.Membership}
                      membershipDiscount={membershipDiscount}
                      setMembershipDiscount={setMembershipDiscount}
                    />
                  ) : discount.Membership ? (
                    <div className="flex flex-col gap-y-2 w-full">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <Text
                            as="label"
                            htmlFor={discount.Membership.UsernameLabel}
                            className="block text-sm font-medium leading-6"
                          >
                            {discount.Membership.UsernameLabel}
                          </Text>
                          <div className="mt-2">
                            <TextInput
                              type="text"
                              placeholder={discount.Membership.UsernameLabel}
                              name={discount.Membership.UsernameLabel}
                              onChange={(e) => {
                                setMembershipDiscount((prev) => ({
                                  ...prev,
                                  ...{
                                    [discount.Id]: {
                                      ...prev[discount.Id],
                                      UsernameLabel: e.target.value,
                                    },
                                  },
                                }));
                              }}
                              id={discount.Membership.UsernameLabel}
                              sharedClasses="w-full"
                              shopsiteClasses="text-sm !rounded"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-3">
                          <Text
                            as="label"
                            htmlFor={discount.Membership.PasswordLabel}
                            className="block text-sm font-medium leading-6 "
                          >
                            {discount.Membership.PasswordLabel}
                          </Text>
                          <div className="mt-2">
                            <TextInput
                              size="2"
                              type="text"
                              name={discount.Membership.PasswordLabel}
                              id={discount.Membership.PasswordLabel}
                              placeholder={discount.Membership.PasswordLabel}
                              onChange={(e) => {
                                setMembershipDiscount((prev) => ({
                                  ...prev,
                                  ...{
                                    [discount.Id]: {
                                      ...prev[discount.Id],
                                      PasswordLabel: e.target.value,
                                    },
                                  },
                                }));
                              }}
                              sharedClasses="w-full"
                              shopsiteClasses="text-sm !rounded"
                            />
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        highContrast
                        disabled={
                          loading ||
                          membershipDiscount[discount.Id].PasswordLabel ===
                            "" ||
                          membershipDiscount[discount.Id].UsernameLabel === ""
                        }
                        onClick={async () => {
                          await handleDiscount(
                            discount.Id,
                            discount.IsRetrospectiveCombo,
                            `${
                              discount.QtyRules.length > 0 &&
                              discount.QtyRules[0].Id
                                ? discount.QtyRules[0].Id
                                : discount.Id
                            }=${
                              discount.QtyRules.length > 0 &&
                              discount.QtyRules[0].Min
                                ? discount.QtyRules[0].Min
                                : 1
                            }`
                          );
                        }}
                        sharedClasses="w-full mt-2"
                        shopsiteClasses="text-sm bg-indigo-600"
                      >
                        {loading ? (
                          <LoadingSpinner
                            size="20"
                            color={isShopsite ? "#ffffff" : "var(--gray-11)"}
                          />
                        ) : (
                          t("apply")
                        )}
                      </Button>
                    </div>
                  ) : discount.QtyRules[0]?.Max !==
                    discount.QtyRules[0]?.Min ? (
                    <DiscountDropdown
                      key={`${discount.Id}-${discount.QtyRules[0].Max}-${discount.QtyRules[0].Title}`}
                      qtyRules={discount.QtyRules[0]}
                      discountId={discount.Id}
                      isRetro={discount.IsRetrospectiveCombo}
                      loading={loading}
                      handleDiscount={handleDiscount}
                    />
                  ) : (
                    <Button
                      disabled={loading}
                      highContrast
                      onClick={async () => {
                        await handleDiscount(
                          discount.Id,
                          discount.IsRetrospectiveCombo,
                          discount.QtyRules.length > 0
                            ? `${discount.QtyRules[0].Id}=${discount.QtyRules[0].Min}`
                            : ""
                        );
                      }}
                      sharedClasses="w-full mt-2"
                      shopsiteClasses="text-sm bg-indigo-600"
                    >
                      {loading ? (
                        <LoadingSpinner
                          size="20"
                          color={isShopsite ? "#ffffff" : "var(--gray-11)"}
                        />
                      ) : (
                        t("apply")
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DiscountList;
